<template>
  <GenericCard
    :locked="locked"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="$v.section.$invalid"
    :dirty="$v.section.$anyDirty"
    title="Identifiers"
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <v-row>
        <v-col>
          <v-row>
            <v-col>
              <v-text-field
                ref="partyANumber"
                v-model="section.a_number"
                v-mask="aNumberMask"
                autocomplete="off"
                :label="aNumberLabel"
                outlined
                :disabled="section.no_a_number"
                :error-messages="getErrorMessages($v.section.a_number)"
                @input="$v.section.a_number.$touch()"
                @blur="$v.section.a_number.$touch()"
              />
            </v-col>
            <v-col cols="auto">
              <v-checkbox
                v-model="section.no_a_number"
                label="None"
                class="mt-3"
                @input="$v.section.no_a_number.$touch()"
                @blur="$v.section.no_a_number.$touch()"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col>
          <v-row>
            <v-col>
              <v-text-field
                ref="partySSNumber"
                v-model="section.ssn"
                v-mask="ssnMask"
                autocomplete="off"
                :label="ssnLabel"
                outlined
                :disabled="section.no_ssn"
                :error-messages="getErrorMessages($v.section.ssn)"
                @input="$v.section.ssn.$touch()"
                @blur="$v.section.ssn.$touch()"
              />
            </v-col>
            <v-col cols="auto">
              <v-checkbox
                v-model="section.no_ssn"
                label="None"
                class="mt-3"
                @input="$v.section.no_ssn.$touch()"
                @blur="$v.section.no_ssn.$touch()"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col>
          <v-text-field
            v-model="section.other_id"
            autocomplete="off"
            label="Other ID"
            outlined
            :error-messages="getErrorMessages($v.section.other_id)"
            @input="$v.section.other_id.$touch()"
            @blur="$v.section.other_id.$touch()"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-text-field
            v-model="section.identifiers_notes"
            autocomplete="off"
            label="Notes"
            outlined
            :error-messages="getErrorMessages($v.section.identifiers_notes)"
            @input="$v.section.identifiers_notes.$touch()"
            @blur="$v.section.identifiers_notes.$touch()"
          />
        </v-col>
      </v-row>
    </v-form>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import { MASKS } from '@/utilities'
import { PARTY_NAMESPACE } from '@/store/party'
import { alertHandler } from '@/alertHandler'
import { rules } from '@/utilities/validation-rules'

import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard'

export default {
  name: 'IdentifiersCard',
  components: { GenericCard },

  props: {
    id: {
      type: String,
      required: true,
    },

    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      $v: useVuelidate(),
      ...alertHandler(),
    }
  },

  data () {
    return {
      aNumberMask: MASKS.ANUMBER,
      cached_a_number: null,
      cached_ssn: null,
      section: {},
      ssnMask: MASKS.SSNUMBER,
    }
  },

  validations () {
    return {
      section: {
        a_number: { isAnumber: helpers.withMessage('A-number not valid', value => rules.aNumRules(value) === true) },
        identifiers_notes: {},
        no_a_number: {},
        no_ssn: {},
        other_id: {},
        ssn: { isSSN: helpers.withMessage('SSN not valid', value => rules.ssnRules(value) === true) },
      },
    }
  },

  computed: {
    aNumberLabel () {
      return this.section.no_a_number ? 'A# (None)' : 'A#'
    },

    cardIdentifier () { return 'identifiers-card' },

    locked: {
      get () {
        return this.peIdentifiersCardLock
      },

      set (value) {
        this.setPeIdentifiersCardLock(value)
      },
    },

    ssnLabel () {
      return this.section.no_ssn ? 'SSN (None)' : 'SSN'
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peIdentifiersCardLock',
      'peParty',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },

    'section.no_a_number' (val) {
      if (val) {
        this.cached_a_number = this.section.a_number
        this.$nextTick().then(() => { this.section.a_number = '' })
      } else if (this.cached_a_number) {
        this.section.a_number = this.cached_a_number
        this.$nextTick().then(() => { this.cached_a_number = null })
      }
    },

    'section.no_ssn' (val) {
      if (val) {
        this.cached_ssn = this.section.ssn
        this.$nextTick().then(() => { this.section.ssn = '' })
      } else if (this.cached_ssn) {
        this.section.ssn = this.cached_ssn
        this.$nextTick().then(() => { this.cached_ssn = null })
      }
    },
  },

  created () {
    this.initPeIdentifiersCard()
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardRefresh () {
      return this.peIdentifiersCardRefresh({ id: this.id })
    },

    async onCardUpdate () {
      return this.peIdentifiersCardUpdate({ id: this.peParty.id, section: this.section })
    },

    async onSectionUnlock () {
      return this.setPeIdentifiersCardLock(false)
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'setPeIdentifiersCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeIdentifiersCard',
      'peIdentifiersCardRefresh',
      'peIdentifiersCardUpdate',
    ]),
  },
}
</script>
